import React, { FC, useCallback, useState } from "react";
import { Checkbox, Dialog, ButtonProps } from "@xapp/components";

import { CopyToClipboard } from "components/StentorUIComponents";

import { BasicIntegrationCard, IntegrationCardProp, IntegrationConnectionProps } from "./BasicIntegrationCard";

export interface SearchLightConnectionProps extends IntegrationConnectionProps {
    integration: "searchlight-feed";
    /**
     * The identifier SearchLight keys this account on. Per their setup docs it is "the
     * {account-id} identifier you use in your bucket keys", i.e. ours to choose — so it is
     * generated from the org and app (or the stored value) and shown read-only, for the
     * operator to copy into SearchLight.
     */
    accountId: string;
    /**
     * Whether this app's leads feed SearchLight.
     */
    enabled: boolean;
}

export interface SearchLightIntegrationCardProps extends IntegrationCardProp {
    /**
     * Used with `appId` to default the account id. Omitted on either side leaves the field
     * empty rather than producing a half-formed prefix.
     */
    organizationId?: string;
    accountId?: string;
    enabled?: boolean;
    /**
     * Save the SearchLight feed configuration.
     */
    onConnect?(props: SearchLightConnectionProps): void;
}

/**
 * The account id we mint for an app.
 *
 * Both halves are required: "undefined__app" or "org__undefined" would write to a real
 * looking but wrong prefix in the feed bucket, and read as a genuine account on
 * SearchLight's side. Better to leave the field empty and let the operator fill it.
 */
export function defaultSearchLightAccountId(organizationId?: string, appId?: string): string {
    const org = (organizationId || "").trim();
    const app = (appId || "").trim();
    return org && app ? `${org}__${app}` : "";
}

export const SearchLightIntegrationCard: FC<SearchLightIntegrationCardProps> = (props) => {
    const { checked, disabled, isLinked, isActive, onDisconnect, onConnect } = props;

    // Derived from props on every render, never copied into state: onboarding can mount this card
    // before it knows the ids (a deep link or refresh restores props from sessionStorage and the
    // fetched app fills them in afterwards, without a remount). Read-only: we mint it, and a
    // mismatch with SearchLight fails silently, so it is not for editing.
    const accountId = props.accountId || defaultSearchLightAccountId(props.organizationId, props.appId);
    // The feed exports PII, so an enabled flag with no account ID resolves to off, the
    // same way the backend reads the stored config.
    const [enabled, setEnabled] = useState<boolean>(props.enabled === true && !!(props.accountId || "").trim());
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const hasAccountId = !!accountId.trim();

    const handleClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();

        if (typeof checked === "boolean") {
            if (typeof props.onClick === "function") {
                props.onClick(e);
            }
        } else {
            setShowDialog(true);
        }
    };

    const handleConnect = useCallback(() => {
        if (typeof onConnect === "function") {
            onConnect({
                integration: "searchlight-feed",
                accountId: accountId.trim(),
                enabled,
            });
        }
        setShowDialog(false);
    }, [onConnect, accountId, enabled]);

    const handleDisconnect = useCallback(() => {
        if (typeof onDisconnect === "function") {
            onDisconnect();
            setShowDialog(false);
        }
    }, [onDisconnect]);

    const actions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: () => {
                setShowDialog(false);
            },
        },
    ];

    if (typeof onConnect === "function") {
        actions.push({
            label: "Connect",
            disabled: !hasAccountId,
            onClick: handleConnect,
        });
    }

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/searchlight/searchlight-logo.svg"
                integrationId="searchlight"
                text="SearchLight"
                href={() => "/integrations/searchlight"}
                onClick={handleClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={typeof onDisconnect === "function" ? handleDisconnect : undefined}
            />
            <Dialog
                active={showDialog}
                actions={actions}
                title="SearchLight Setup"
                onClose={() => {
                    setShowDialog(false);
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                    <div>
                        <p>
                            Feed your captured leads to SearchLight Digital for revenue attribution across your
                            marketing channels.
                        </p>
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <CopyToClipboard
                            textToCopy={accountId}
                            inputProps={{ label: "SearchLight Account ID" }}
                            tooltipText="Click to copy the account ID"
                        />
                        <p style={{ color: "#666", fontSize: "12px", marginTop: "4px" }}>
                            We generate this ID for you. Copy it into SearchLight so they can find your leads. If the two
                            do not match, no error is shown; your data simply will not appear in SearchLight.
                        </p>
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Checkbox
                            checked={enabled}
                            disabled={!hasAccountId}
                            label="Send leads to SearchLight"
                            onChange={(value) => setEnabled(value)}
                        />
                        <p style={{ fontSize: "12px", marginTop: "4px" }}>
                            Turning this on sends your leads&apos; personal information — names, emails, phone numbers,
                            addresses, and full conversation transcripts — to SearchLight Digital, a third party.
                        </p>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
